import {Component, OnInit} from '@angular/core';
import {BasicPageComponent} from "../../../x-components/basic-page/basic-page.component";
import {CompanyHttpService} from "../../../owner/x-http-requests/company-http.service";
import {CompanyDto} from "../../../models/admin_owner/company/company-dto";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {ContractHttpService} from "../_services/contract-http.service";
import {CompanyEdto} from "../../company/x-models/company-edto";
import {
  ContractBase,
  contractCoversDay,
  contractEndedBefore,
  contractStartsAfter
} from "../../company/x-models/contract-base";
import {Tuple} from "../../../models/_generic/tuple";
import {CustomerValues} from "../../company/x-models/customer-values";
import {FormsModule} from "@angular/forms";
import {
  companyToday,
  DayKey,
  DEFAULT_COMPANY_TIME_ZONE,
  isDayKey,
  startOfMonth
} from "../../../_shared/time/company-day";
import {DayKeyPipe} from "../../../_shared/time/day-key.pipe";
import {ContractCreateModel} from "../_models/contract-create-model";
import {ContractInformationComponent} from "../_components/contract-information/contract-information.component";

@Component({
  selector: 'app-contract-manager',
  imports: [
    BasicPageComponent,
    NgForOf,
    NgClass,
    NgIf,
    DayKeyPipe,
    FormsModule,
    ContractInformationComponent
  ],
  templateUrl: './contract-manager.component.html',
  styleUrl: './contract-manager.component.scss'
})
export class ContractManagerComponent implements OnInit {
  constructor(private companyHttpService: CompanyHttpService,
              private contractHttpService: ContractHttpService,
              private gas: GlobalAlertService) {
  }

  ngOnInit(): void {
    this.load();
    this.company_contract.reset();
  }

  load() {
    this.companyHttpService.list()
      .withLoadingIndicator()
      .subscribe({
        next: companies => {
          this.companies_all = companies;
          this.filter_companies()
          this.company_select(this.companies_filtered[this.companies_filtered.length - 1])
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Unternehmen", AlertLevel.error);
        }
      })
  }

  filter_companies() {
    this.companies_filtered = Object.assign([], this.companies_all);
  }

  companies_all: CompanyDto[] = [];

  companies_filtered: CompanyDto[] = [];

  private _company_selected: CompanyDto | undefined;
  set company_selected(value: CompanyDto | undefined) {
    this._company_selected = value;
    if (this._company_selected) {
      this.company_load(this._company_selected);
    }
  }

  get company_selected(): CompanyDto | undefined {
    return this._company_selected;
  }

  company_select(company: CompanyDto) {
    this.company_selected = company;
  }

  private _company_details: CompanyEdto | undefined;
  get company_details(): CompanyEdto | undefined {
    return this._company_details;
  }

  set company_details(value: CompanyEdto | undefined) {
    this._company_details = value;
    this.company_details_contract_current = this.company_contract_current();
    this.company_details_contract_past =
      this._company_details?.contracts.filter(x => contractEndedBefore(x, this.today)) || [];
    this.company_details_contract_upcoming =
      this._company_details?.contracts.filter(x => contractStartsAfter(x, this.today)) || [];
  }

  company_details_contract_past: ContractBase[] = [];
  company_details_contract_upcoming: ContractBase[] = [];

  company_details_contract_current: ContractBase | undefined;

  company_values: Tuple<CustomerValues, CustomerValues> | undefined;

  company_load(company: CompanyDto) {
    this.contractHttpService.getCompany(company.id)
      .withLoadingIndicator()
      .subscribe({
        next: company => {
          this.company_details = company;
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Unternehmensdetails", AlertLevel.error);
          this.company_details = undefined;
        }
      })

    this.contractHttpService.getCustomerValues(company.id)
      .withLoadingIndicator()
      .subscribe({
        next: values => {
          this.company_values = values;
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Laden der Kundenwerte", AlertLevel.error);
          this.company_values = undefined;
        }
      })
  }

  /**
   * Today, for sorting contracts into running, upcoming and past (#802). This screen inspects any company and does
   * not know its zone, so it uses the platform default; every customer is in Berlin today.
   */
  today: DayKey = companyToday(DEFAULT_COMPANY_TIME_ZONE);

  company_contract_current(): ContractBase | undefined {
    return this.company_details?.contracts.find(x => contractCoversDay(x, this.today))
  }

  company_contract_edit_mode: boolean = false;
  company_contract: company_contract = new company_contract();

  save(): void {
    if (!this.company_selected) return;
    (this.company_contract.id
        ? this.contractHttpService.update(this.company_selected.id, this.company_contract.id, this.company_contract.toModel())
        : this.contractHttpService.create(this.company_selected.id, this.company_contract.toModel())
    ).withLoadingIndicator().subscribe({
      next: company => {
        this.company_details = company;
        this.gas.createAndShow("Erfolg", "Vertrag erfolgreich gespeichert", AlertLevel.success);
        this.company_contract_edit_mode = false;
        this.company_contract.reset();
      },
      error: err => {
        console.error(err);
        this.gas.createAndShow("Fehler", "Fehler beim Speichern des Vertrags", AlertLevel.error);
      }
    })
  }

  delete(): void {
    if (!this.company_selected || !this.company_contract.id) return;
    // popup if sure
    let sure = confirm("Sind Sie sicher, dass Sie den Vertrag löschen möchten?");
    if (!sure) return;

    this.contractHttpService.delete(this.company_selected.id, this.company_contract.id)
      .withLoadingIndicator()
      .subscribe({
        next: company => {
          this.company_details = company;
          this.gas.createAndShow("Erfolg", "Vertrag erfolgreich gelöscht", AlertLevel.success);
          this.company_contract_edit_mode = false;
          this.company_contract.reset();
        },
        error: err => {
          console.error(err);
          this.gas.createAndShow("Fehler", "Fehler beim Löschen des Vertrags", AlertLevel.error);
        }
      })
  }
}

/**
 * Editable view-model for a contract. The two days are plain `yyyy-MM-dd` strings, bound straight to the date
 * inputs and posted as they are (#802); the end is the contract's last day, included.
 */
class company_contract {
  id: string | undefined;
  start: DayKey = '';
  end: DayKey = '';
  shard_count: number = 1;
  device_count: number = 3;
  device_minimum_months: number = 2;
  /**
   * Parallel virtual events (#664). `-1` = unbegrenzt, `0` = keine.
   *
   * Defaults to `0`, not to a helpful number: virtual events are a licensed feature, and a new contract must
   * grant them deliberately rather than by the form's convenience.
   */
  virtual_event_count: number = 0;

  /** A new contract runs from the 1st of the current month to 31.12. of the same year. */
  reset_dates() {
    this.start = startOfMonth(companyToday(DEFAULT_COMPANY_TIME_ZONE));
    this.end = `${this.start.slice(0, 4)}-12-31`;
  }

  reset() {
    this.reset_dates();
    this.id = undefined;
    this.shard_count = 1;
    this.device_count = 3;
    this.device_minimum_months = 2;
    this.virtual_event_count = 0;
  }

  load(contract: ContractBase) {
    this.id = contract.id;
    // Anything that is not a day (an instant from a server built before #802) leaves the field empty rather
    // than guessing a day in the browser's zone.
    this.start = isDayKey(contract.validFrom) ? contract.validFrom : '';
    this.end = isDayKey(contract.validTo) ? contract.validTo : '';
    this.shard_count = contract.shardCount;
    this.device_count = contract.deviceCount;
    this.device_minimum_months = contract.minimalMonthsPerDevice;
    this.virtual_event_count = contract.virtualEventCount;
  }

  toModel(): ContractCreateModel {
    return new ContractCreateModel(this.start, this.end, this.shard_count, this.device_count, this.device_minimum_months, this.virtual_event_count);
  }
}
